import React, { useCallback, useMemo } from "react";
import Link from "next/link";
import { IconButton, Tooltip } from "@material-ui/core";
import {
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  SaveAlt as SaveAltIcon
  // Visibility as VisibilityIcon
} from "@material-ui/icons";
import { ActiveStatus } from "src/apps/commons/dataDisplays";
import { DataTable } from "src/apps/commons/dataTable";
import { download } from "src/apps/commons/fileUploads/api";
import { ListPaper } from "src/apps/commons/papers";
import { downloadFile, formatDateTime } from "src/utils";
import * as Api from "../api";
import {
  IRemyndManualBrief,
  IRemyndManualPaginateOptionKey,
  IRemyndManualPaginateReq
} from "../types";
import { constants, paths } from "../utils";
import ListFilterDialog from "./ListFilterDialog";

const ListContainers: React.FC<unknown> = _ => {
  const itemToString = useCallback(
    (data: IRemyndManualBrief) => `${data.nameTh}`,
    []
  );

  const ManageCell = useCallback(
    (data: IRemyndManualBrief, isLoading: boolean) => (
      <div>
        {isLoading ? (
          <IconButton
            aria-label="Edit"
            key={`loading_manage_${data.id}`}
            disabled
          >
            <EditIcon />
          </IconButton>
        ) : (
          // <span>
          //   <Link
          //     href={`${paths.show}?id=${data.id}`}
          //     key={`manage_show${data.id}`}
          //   >
          //     <a>
          //       <IconButton aria-label="Show">
          //         <Tooltip title={`แสดง ${constants.label}`} placement="top">
          //           <VisibilityIcon />
          //         </Tooltip>
          //       </IconButton>
          //     </a>
          //   </Link>
          //   <Link
          //     href={`${paths.edit}?id=${data.id}`}
          //     key={`manage_edit${data.id}`}
          //   >
          //     <a>
          //       <IconButton aria-label="Edit">
          //         <Tooltip title={`แก้ไข ${constants.label}`} placement="top">
          //           <EditIcon />
          //         </Tooltip>
          //       </IconButton>
          //     </a>
          //   </Link>
          // </span>
          <Link href={`${paths.edit}?id=${data.id}`} key={`manage_${data.id}`}>
            <a>
              <IconButton aria-label="Edit">
                <Tooltip title={`แก้ไข${constants.label}`} placement="top">
                  <EditIcon />
                </Tooltip>
              </IconButton>
            </a>
          </Link>
        )}
      </div>
    ),
    []
  );

  const LinkCell = useCallback(
    (data: IRemyndManualBrief) => (
      <>
        {data?.link ? (
          <Tooltip title={`${data.link}`} placement="top">
            <IconButton
              aria-label="Link"
              href={data.link}
              target="_blank"
              rel="noreferrer"
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton aria-label="Link" disabled>
            <OpenInNewIcon />
          </IconButton>
        )}
      </>
    ),
    []
  );

  const downloadAndSave = async (fileId: string) => {
    const resp = await download(fileId);
    await downloadFile(resp);
  };

  const DownloadCell = useCallback((data: IRemyndManualBrief) => {
    const { fileId } = data;
    return (
      <IconButton
        aria-label="Download"
        onClick={async () => {
          if (fileId) await downloadAndSave(fileId);
        }}
        disabled={!fileId}
      >
        <SaveAltIcon />
      </IconButton>
    );
  }, []);

  const ActiveCell = useCallback(
    (data: IRemyndManualBrief) => <ActiveStatus value={data.active} />,
    []
  );

  const defaultCriteria = useMemo(
    () =>
      ({
        paginate: {
          page: 1,
          pageSize: 10,
          order: { optionKey: "UPDATED_AT", desc: true }
        }
      } as IRemyndManualPaginateReq),
    []
  );

  return (
    <ListPaper>
      <DataTable<
        IRemyndManualBrief,
        IRemyndManualPaginateReq,
        IRemyndManualPaginateOptionKey,
        "MANAGE"
      >
        pathCreate={paths.create}
        searchTermLabel={"ค้นหาจาก ชื่อ (TH/EN)"}
        listEntity={Api.list}
        deleteEntities={Api.deleteMultiple}
        itemToString={itemToString}
        filterDialog={ListFilterDialog}
        defaultCriteria={defaultCriteria}
        columns={[
          {
            id: "MANAGE",
            label: "จัดการ",
            width: 60,
            style: { paddingLeft: 0, paddingRight: 8 },
            align: "center",
            renderCell: ManageCell
          },
          {
            id: "NAME_TH",
            label: "ชื่อ (TH)",
            minWidth: 160,
            orderable: true,
            renderCell: data => data.nameTh
          },
          {
            id: "NAME_EN",
            label: "ชื่อ (EN)",
            minWidth: 160,
            orderable: true,
            renderCell: data => data.nameEn
          },
          {
            id: "DESC_TH",
            label: "รายละเอียด (TH)",
            minWidth: 220,
            orderable: true,
            renderCell: data => data.descTh ?? "ไม่ระบุ",
            renderFaded: data => !data.descTh
          },
          {
            id: "DESC_EN",
            label: "รายละเอียด (EN)",
            minWidth: 220,
            orderable: true,
            renderCell: data => data.descEn ?? "ไม่ระบุ",
            renderFaded: data => !data.descEn
          },
          {
            id: "LINK",
            label: "Link",
            width: 120,
            align: "center",
            renderCell: LinkCell
          },
          {
            id: "FILE_UPLOAD",
            label: "ไฟล์อัพโหลด",
            width: 120,
            align: "center",
            renderCell: DownloadCell
          },
          {
            id: "ACTIVE",
            label: "Active",
            width: 80,
            orderable: true,
            align: "center",
            renderCell: ActiveCell
          },
          {
            id: "BIND_WITH_USER_PRODUCT_COUNT",
            label: "จำนวน User Product ที่ผูก",
            width: 160,
            orderable: true,
            align: "center",
            renderCell: data => `${data.bindWithUserProductCount}`,
            renderFaded: data => !data.bindWithUserProductCount
          },
          {
            id: "BIND_WITH_REMYND_PRODUCT_COUNT",
            label: "จำนวน Remynd Product ที่ผูก",
            width: 160,
            orderable: true,
            align: "center",
            renderCell: data => `${data.bindWithRemyndProductCount}`,
            renderFaded: data => !data.bindWithRemyndProductCount
          },
          {
            id: "CREATED_AT",
            label: "สร้างเมื่อ",
            width: 200,
            orderable: true,
            renderCell: data => formatDateTime(data.createdAt)
          },
          {
            id: "UPDATED_AT",
            label: "แก้ไขล่าสุดเมื่อ",
            width: 200,
            orderable: true,
            renderCell: data => formatDateTime(data.updatedAt)
          }
        ]}
      />
    </ListPaper>
  );
};

export default ListContainers;
